import { useState, useEffect, useRef } from "react";
import {
  Mail, Phone, MapPin, Instagram, Youtube, Facebook,
  Car, Hotel, Compass, Send, ChevronRight, Mountain
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// ─── Floating particle background ────────────────────────────────────
const Particle = ({ style }) => (
  <div
    className="absolute rounded-full opacity-20 pointer-events-none"
    style={style}
  />
);

// ─── Service pill ─────────────────────────────────────────────────────
const ServiceTag = ({ icon: Icon, label, delay }) => (
  <div
    className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#CC3316]/40 bg-[#CC3316]/10 text-[#CC3316] text-[14px] font-medium backdrop-blur-sm animate-fadeIn"
    style={{ animationDelay: delay }}
  >
    <Icon className="w-4 h-4 text-[#CC3316]" />
    {label}
  </div>
);

// ─── Info card ────────────────────────────────────────────────────────
const InfoCard = ({ icon: Icon, title, lines, delay }) => (
  <div
    className="group flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#CC3316]/50 hover:bg-[#CC3316]/5 transition-all duration-300 animate-slideUp"
    style={{ animationDelay: delay }}
  >
    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#CC3316] to-[#FF6B35] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-[#CC3316] font-semibold text-sm uppercase tracking-wider mb-1">{title}</p>
      {lines.map((l, i) => (
        <p key={i} className="text-xs leading-relaxed">{l}</p>
      ))}
    </div>
  </div>
);

// ─── Input ────────────────────────────────────────────────────────────
const FormInput = ({ label, type = "text", name, value, onChange, placeholder, required, multiline }) => {
  const base =
    "w-full bg-white/5 border border-white text-black placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 focus:bg-amber-400/5 transition-all duration-200";
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm uppercase tracking-widest font-medium">{label}</label>
      {multiline ? (
        <textarea
          name={name} value={value} onChange={onChange}
          placeholder={placeholder} required={required}
          rows={5}
          className={`${base} resize-none`}
        />
      ) : (
        <input
          type={type} name={name} value={value}
          onChange={onChange} placeholder={placeholder}
          required={required}
          className={base}
        />
      )}
    </div>
  );
};

// ─── Main Contact Page ────────────────────────────────────────────────
export default function ContactPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", query: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [particles] = useState(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      width: Math.random() * 6 + 2,
      top: Math.random() * 100,
      left: Math.random() * 100,
      color: i % 3 === 0 ? "#CC3316" : i % 3 === 1 ? "#FF6B35" : "#FBAF5A",
      animDur: Math.random() * 8 + 6,
      animDelay: Math.random() * 4,
    }))
  );

 const handleChange = (e) => {
  let { name, value } = e.target;

  if (name === "phone") {
    value = value.replace(/\D/g, ""); // only digits
    if (value.length > 10) return;    // max 10 digits
  }

  setFormData(prev => ({ ...prev, [name]: value }));
};

 const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/enquiry/create`,
      formData
    );

    if (res.data.success) {
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          query: "",
        });
      }, 3500);
    }

  } catch (error) {
    console.log(error);
    alert("Failed to send enquiry");
  }
};
  const services = [
    { icon: Car, label: "Cab Booking" },
    { icon: Hotel, label: "Hotel Stays" },
    { icon: Compass, label: "Tour Packages" },
    { icon: Mountain, label: "Pilgrim Tours" },
  ];

  return (
    <>
      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500&display=swap');

        * { box-sizing: border-box; }

        .font-display { font-family: 'Playfair Display', serif; }
        .font-body    { font-family: 'DM Sans', sans-serif; }

        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50%       { transform: translateY(-20px) scale(1.05); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        .animate-fadeIn  { animation: fadeIn  0.6s ease both; }
        .animate-slideUp { animation: slideUp 0.7s ease both; }

        .shimmer-text {
          background: linear-gradient(90deg, #f59e0b, #fb923c, #fbbf24, #f59e0b);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        .card-glow {
          box-shadow: 0 0 40px rgba(245, 158, 11, 0.08), 0 20px 60px rgba(0,0,0,0.4);
        }

        .btn-glow:hover {
          box-shadow: 0 0 30px rgba(245,158,11,0.5);
        }

        .divider-ornament {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 20px 0;
        }
        .divider-ornament::before,
        .divider-ornament::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(245,158,11,0.4));
        }
        .divider-ornament::after {
          background: linear-gradient(to left, transparent, rgba(245,158,11,0.4));
        }

        /* Odisha temple pattern overlay */
        .temple-bg {
          background-image:
            radial-gradient(ellipse at 20% 50%, rgba(245,158,11,0.07) 0%, transparent 60%),
            radial-gradient(ellipse at 80% 20%, rgba(234,88,12,0.07) 0%, transparent 60%),
            radial-gradient(ellipse at 60% 80%, rgba(251,191,36,0.05) 0%, transparent 50%);
        }

        input:-webkit-autofill,
        textarea:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 1000px rgba(255,255,255,0.05) inset !important;
          -webkit-text-fill-color: white !important;
        }
      `}</style>

      <div className="font-body  temple-bg relative overflow-hidden">

        {/* ── Floating particles ── */}
        {particles.map(p => (
          <Particle key={p.id} style={{
            width: p.width, height: p.width,
            top: `${p.top}%`, left: `${p.left}%`,
            backgroundColor: p.color,
            animation: `float ${p.animDur}s ${p.animDelay}s ease-in-out infinite`,
          }} />
        ))}

        {/* ── Hero header ── */}
        <div className="relative pt-10 px-4 sm:px-8 lg:px-20 text-center overflow-hidden">
          {/* Decorative ring */}
          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full border border-amber-400/10 pointer-events-none" />
          <div className="absolute top-16 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full border border-amber-400/10 pointer-events-none" />

          <p className="text-[#CC3316] text-sm uppercase tracking-[0.3em] font-medium mb-3 animate-fadeIn">
            ✦ Berhampur, Odisha ✦
          </p>
          <h1 className="font-display text-2xl md:text-4xl font-black leading-tight mb-4 animate-fadeIn" style={{ animationDelay: "0.1s" }}>
            <span className="">Get In </span>
            <span className="shimmer-text text-[#CC3316]">Touch</span>
          </h1>
          <p className="text-base md:text-lg max-w-xl mx-auto animate-fadeIn" style={{ animationDelay: "0.2s" }}>
            Plan your dream journey across Odisha — temples, beaches, forests & beyond. 
            We handle every mile.
          </p>

          {/* Service tags */}
          {/* <div className="flex flex-wrap text-[#CC3316]  justify-center gap-3 mt-8 animate-fadeIn" style={{ animationDelay: "0.3s" }}>
            {services.map((s, i) => (
              <ServiceTag key={s.label} icon={s.icon} label={s.label} delay={`${0.3 + i * 0.07}s`} />
            ))}
          </div> */}
        </div>

        {/* ── Main content ── */}
        <div className="w-full mx-auto  py-10 px-4 sm:px-8 lg:px-20 ">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* ── LEFT PANEL (2 cols) ── */}
            <div className="lg:col-span-2 flex flex-col gap-5">

              {/* Brand card */}
              <div className="card-glow rounded-3xl  p-7 backdrop-blur-sm animate-slideUp">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#CC3316] to-orange-600 flex items-center justify-center">
                    <Mountain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-display font-bold  text-lg leading-tight">Maa Bhairabi</p>
                    <p className="text-[#CC3316] text-xs tracking-wider">Tour & Travel</p>
                  </div>
                </div>
                <p className=" text-lg leading-relaxed">
                  Your trusted travel companion for pilgrimages, leisure tours, cab rentals & hotel bookings across Odisha and beyond. Based in the heart of Berhampur.
                </p>
                <div className="divider-ornament">
                  <span className="text-amber-400 text-xs">✦</span>
                </div>
                <p className=" text-sm italic">
                  "Where every journey becomes a memory"
                </p>
              </div>

              {/* Contact info cards */}
              <div className="card-glow rounded-3xl p-6 backdrop-blur-sm flex flex-col gap-3">
                <p className="text-[#CC3316] text-xs uppercase tracking-[0.2em] font-semibold mb-1">Reach Us</p>

                <InfoCard
                  icon={Phone} title="Call / WhatsApp"
                  lines={["+91 96928 83788", "+91 89174 32299"]}
                  delay="0.2s"
                />
                <InfoCard
                  icon={Mail} title="Email"
                  lines={["maabhairabittravels@gmail.com"]}
                  delay="0.3s"
                />
                <InfoCard
                  icon={Car} title="Cab Booking"
                  lines={["Local, outstation & airport drops", "AC & non-AC vehicles available"]}
                  delay="0.35s"
                />
                <InfoCard
                  icon={Hotel} title="Hotel Bookings"
                  lines={["Budget to luxury stays", "Berhampur & across Odisha"]}
                  delay="0.4s"
                />
                <InfoCard
                  icon={MapPin} title="Office"
                  lines={["Berhampur (Brahmapur), Ganjam", "Odisha — 760 001"]}
                  delay="0.45s"
                />
              </div>

              {/* Social */}
              <div className="card-glow rounded-3xl  p-6 backdrop-blur-sm animate-slideUp" style={{ animationDelay: "0.5s" }}>
                <p className="text-[#CC3316] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Follow Our Journey</p>
                <div className="flex gap-4">
                  {[
                    { icon: Instagram, label: "Instagram", color: "from-pink-500 to-orange-400", href: "https://www.instagram.com/maabhairabittravels" },
                    { icon: Youtube,   label: "YouTube",   color: "from-red-500 to-red-700",     href: "https://youtube.com" },
                    { icon: Facebook,  label: "Facebook",  color: "from-blue-500 to-blue-700",   href: "https://facebook.com" },
                  ].map(({ icon: Icon, label, color, href }) => (
                    <a
                      key={label} href={href} target="_blank" rel="noreferrer"
                      className={`flex-1 flex flex-col items-center gap-2 py-3 rounded-2xl bg-gradient-to-br ${color} bg-opacity-10 border border-white/10 hover:border-amber-400/40 hover:scale-105 transition-all duration-200 group`}
                    >
                      <Icon className="w-5 h-5 text-white" />
                      <span className="text-white text-[10px] font-medium">{label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* ── RIGHT PANEL (3 cols) ── */}
            <div className="lg:col-span-3 flex flex-col gap-6">

              {/* Form */}
              <div className="card-glow rounded-3xl border border-white/10 p-7 md:p-9 backdrop-blur-sm animate-slideUp" style={{ animationDelay: "0.15s" }}>

                {/* Form header */}
                <div className="mb-7">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-[#CC3316]/60 to-transparent" />
                    <Compass className="w-4 h-4 text-[#CC3316]" />
                    <div className="h-px flex-1 bg-gradient-to-l from-[#CC3316]/60 to-transparent" />
                  </div>
                  <h2 className="font-display text-3xl font-bold text-[#CC3316] mt-3">
                    Plan Your Trip
                  </h2>
                  <p className=" text-sm mt-1">
                    Tell us where you want to go — we'll make it happen.
                  </p>
                </div>

                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center animate-fadeIn">
                    <div className="relative w-16 h-16 mb-5">
                      <div className="w-16 h-16 rounded-full bg-[#CC3316]/20 flex items-center justify-center">
                        <Send className="w-7 h-7 text-[#CC3316]" />
                      </div>
                      <div className="absolute inset-0 rounded-full border-2 border-[#CC3316]" style={{ animation: "pulse-ring 1.2s ease-out infinite" }} />
                    </div>
                    <h3 className="font-display text-2xl text-white font-bold mb-2">Message Sent!</h3>
                    <p className="text-gray-400 text-sm">Our team will reach out within 24 hours. Get ready for your adventure! 🏔️</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <FormInput label="Full Name" name="name" value={formData.name}
                        onChange={handleChange} placeholder="Your name" required />
                      <FormInput label="Phone / WhatsApp" type="tel" name="phone"
                        value={formData.phone} onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX" required />
                    </div>

                    <FormInput label="Email Address" type="email" name="email"
                      value={formData.email} onChange={handleChange}
                      placeholder="you@example.com" />

                    {/* Service selector */}
                    {/* <div className="flex flex-col gap-1.5">
                      <label className=" text-sm uppercase tracking-widest font-medium">Service Needed</label>
                      <select
                        name="service" value={formData.service} onChange={handleChange}
                        className="w-full bg-white/5 border border-white/15 text-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 focus:bg-amber-400/5 transition-all duration-200 appearance-none"
                        style={{ colorScheme: "dark" }}
                      >
                        <option value="" className="bg-[#1a1a2e]">Select a service...</option>
                        <option value="cab" className="bg-[#1a1a2e]">🚗 Cab Booking</option>
                        <option value="hotel" className="bg-[#1a1a2e]">🏨 Hotel Booking</option>
                        <option value="tour" className="bg-[#1a1a2e]">🧭 Tour Package</option>
                        <option value="pilgrim" className="bg-[#1a1a2e]">🛕 Pilgrimage Tour</option>
                        <option value="custom" className="bg-[#1a1a2e]">✨ Custom Trip</option>
                      </select>
                    </div> */}

                    <FormInput label="Message / Destination" name="query"
                      value={formData.query} onChange={handleChange}
                      placeholder="Tell us your travel dates, destination, or any special requirements..."
                      multiline />

                    <button
                      type="submit"
                      className="btn-glow w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-gradient-to-r from-[#CC3316] to-[#FF6B35] text-white font-semibold text-base transition-all duration-300 hover:from-amber-300 hover:to-orange-400 hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                    >
                      <Send className="w-5 h-5" />
                      Send Enquiry
                      <ChevronRight className="w-4 h-4" />
                    </button>

                    <p className="text-center text-gray-600 text-xs">
                      Or WhatsApp us directly at <span className="text-[#CC3316] font-medium">+91 96928 83788</span>
                    </p>
                  </form>
                )}
              </div>

              {/* Map */}
              <div
                className="card-glow rounded-3xl overflow-hidden border border-white/10 animate-slideUp relative"
                style={{ animationDelay: "0.3s", height: "280px" }}
              >
                {/* Map label */}
                <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-[#0d1117]/80 backdrop-blur-sm px-3 py-1.5 rounded-xl border border-white/10">
                  <MapPin className="w-3.5 h-3.5 text-[#CC3316]" />
                  <span className="text-white text-xs font-medium">Berhampur, Odisha</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30468.99!2d84.7941!3d19.3150!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a3d4ac0962e3051%3A0x400db50a84c6420!2sBerhampur%2C%20Odisha!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                  width="100%" height="100%"
                  style={{ border: 0, filter: "invert(90%) hue-rotate(200deg) saturate(0.6)" }}
                  allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Maa Bhairabi Tour & Travel — Berhampur"
                />
              </div>

              {/* Quick services row */}
              <div className="grid grid-cols-3 gap-4 animate-slideUp" style={{ animationDelay: "0.4s" }}>
                {[
                  { icon: Car,     label: "Cab Hire",    sub: "24/7 Available",    color: "from-orange-500/20 to-amber-500/10", path: "/car"  },
                  { icon: Hotel,   label: "Hotels",      sub: "Best Rates",        color: "from-teal-500/20 to-cyan-500/10"   },
                  { icon: Compass, label: "Tour Pkgs",   sub: "Custom Itinerary",  color: "from-purple-500/20 to-pink-500/10", path: "/tour" },
                ].map(({ icon: Icon, label, sub, color, path }) => (
                  <div
                    key={label}
                    onClick={() => navigate(path)}
                    className={`flex flex-col items-center text-center p-4 rounded-2xl bg-gradient-to-br ${color} border border-white/10 hover:border-amber-400/40 hover:scale-105 transition-all duration-200 cursor-pointer`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-2">
                      <Icon className="w-5 h-5 text-[#CC3316]" />
                    </div>
                    <p className=" text-sm font-semibold">{label}</p>
                    <p className="text-gray-500 text-[10px] mt-0.5">{sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

    
      </div>
    </>
  );
}